// src/pages/CouponClaim.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import FullBleed from '../components/FullBleed';
import StageFrame from '../components/StageFrame';
import PhoneInput from '../components/PhoneInput';
import CouponQrBlock from '../components/CouponQrBlock';
import bg from '../assets/card.png';
import { CouponLangSwitch, CouponLocaleProvider, useCouponLocale } from '../context/CouponLocaleContext';
import { useLiffLine } from '../context/LiffLineContext';
import { resolveCouponName, resolveCouponDescription } from '../utils/couponContent';
import {
  buildCouponClaimPath,
  buildCouponLinkReturnUrl,
  clearAutoLineClaimFlag,
  clearCouponClaimDraft,
  clearPendingClaimResult,
  isCouponLineClaimMode,
  loadCouponClaimDraft,
  loadPendingClaimResult,
  prepareCouponClaimDirectVisit,
  resolveCouponClaimCode,
  saveCouponClaimDraft,
  shouldAutoLineClaim,
  waitForLineSub,
  getLineIdToken,
  maskEmailForDisplay,
} from '../utils/couponLiff';
import { couponClaimStyles } from '../utils/couponClaimStyles';
import { resolveDisplayCode } from '../utils/redeemDisplayCode';

axios.defaults.withCredentials = true;

const RESEND_COOLDOWN_SEC = 60;

function stepIndex(status) {
  if (status === 'verify') return 1;
  if (status === 'success') return 2;
  return 0;
}

function CouponClaimBody() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, lang } = useCouponLocale();
  const { lineSub: liffLineSub } = useLiffLine();
  const lineInflightRef = useRef(false);
  const lineAutoStartedRef = useRef(false);
  const [lineErrorCode, setLineErrorCode] = useState('');
  const [claimViaLine, setClaimViaLine] = useState(() => isCouponLineClaimMode());
  const [code, setCode] = useState(() => {
    prepareCouponClaimDirectVisit();
    return resolveCouponClaimCode();
  });

  useEffect(() => {
    prepareCouponClaimDirectVisit();
  }, []);

  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [verifySessionId, setVerifySessionId] = useState(null);
  const [resendSec, setResendSec] = useState(0);
  const [status, setStatus] = useState('form');
  const [busy, setBusy] = useState(null);
  const [message, setMessage] = useState('');
  const [couponInfo, setCouponInfo] = useState(null);
  const [loadingCoupon, setLoadingCoupon] = useState(false);
  const [claimResult, setClaimResult] = useState(null);

  const STEPS = useMemo(() => ([
    { key: 'form', label: t('stepForm') },
    { key: 'verify', label: t('stepVerify') },
    { key: 'success', label: t('stepSuccess') },
  ]), [t]);

  const applyRateLimitFromResponse = (errOrData, fallbackSec = RESEND_COOLDOWN_SEC) => {
    const sec = errOrData?.retryAfterSec ?? fallbackSec;
    const n = Math.max(1, Number(sec) || fallbackSec);
    setResendSec(n);
    setMessage(t('rateLimit', { sec: n }));
  };

  const mapApiError = (err) => {
    const key = err?.response?.data?.error;
    if (key && t(key) !== key) return t(key);
    return null;
  };

  useEffect(() => {
    const resolved = resolveCouponClaimCode();
    setCode((prev) => (prev === resolved ? prev : resolved));
    if (isCouponLineClaimMode()) setClaimViaLine(true);
  }, [location.search]);

  useEffect(() => {
    if (location.pathname === '/coupon-claim') return;
    const resolved = resolveCouponClaimCode();
    if (!resolved) return;
    navigate(buildCouponClaimPath(resolved), { replace: true });
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!code) return;
    const pending = loadPendingClaimResult(code);
    if (pending) {
      clearPendingClaimResult();
      clearAutoLineClaimFlag();
      setClaimViaLine(true);
      setClaimResult(pending);
      setStatus('success');
      setMessage(t('claimSuccess', { name: pending.name || t('couponsTitle') }));
      clearCouponClaimDraft(code);
      return;
    }
    const draft = loadCouponClaimDraft(code);
    if (draft && draft.status !== 'success') {
      if (draft.phone) setPhone(draft.phone);
      if (draft.email) setEmail(draft.email);
      if (draft.verifySessionId) setVerifySessionId(draft.verifySessionId);
      if (draft.status === 'verify') setStatus('verify');
    }
  }, [code, t]);

  useEffect(() => {
    if (!code || status === 'success') return;
    saveCouponClaimDraft(code, { phone, email, verifySessionId, status });
  }, [code, phone, email, verifySessionId, status]);

  useEffect(() => {
    if (resendSec <= 0) return undefined;
    const timer = setTimeout(() => setResendSec((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendSec]);

  useEffect(() => {
    if (!code) {
      setLoadingCoupon(false);
      setStatus('error');
      setMessage(t('NO_CODE'));
      return;
    }
    let cancelled = false;
    setStatus((prev) => (prev === 'success' ? prev : 'form'));
    setMessage((prev) => (prev === t('NO_CODE') ? '' : prev));
    setLoadingCoupon(true);
    axios.get('/api/coupon/info', { params: { code } })
      .then(({ data }) => {
        if (cancelled) return;
        if (data?.ok && data?.coupon) {
          setCouponInfo(data.coupon);
          setStatus((prev) => (prev === 'success' ? prev : 'form'));
        } else {
          setStatus('error');
          setMessage(t(data?.error) || t('NOT_FOUND'));
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus('error');
        setMessage(mapApiError(err) || t('loadFail'));
      })
      .finally(() => {
        if (!cancelled) setLoadingCoupon(false);
      });
    return () => { cancelled = true; };
  }, [code, t]);

  const handleClaimSuccess = (data, { viaLine = false } = {}) => {
    clearCouponClaimDraft(code);
    clearAutoLineClaimFlag();
    if (viaLine) setClaimViaLine(true);
    setClaimResult(data);
    setStatus('success');
    setBusy(null);
    const couponName = resolveCouponName({ ...couponInfo, ...data }, lang)
      || data?.name
      || t('couponsTitle');
    setMessage(t('claimSuccess', { name: couponName }));
  };

  const runLineClaim = useCallback(async () => {
    if (!code || lineInflightRef.current || loadingCoupon) return;
    lineInflightRef.current = true;
    setClaimViaLine(true);
    setBusy('line');
    setLineErrorCode('');
    setMessage(t('lineResolvingSub'));

    try {
      const sub = await waitForLineSub({ hint: liffLineSub, maxWaitMs: 8000 });
      const idToken = getLineIdToken();
      if (!sub && !idToken) {
        setLineErrorCode('NO_LINE_SUB');
        setMessage(t('lineNoSub'));
        return;
      }

      setMessage(t('lineAutoClaim'));
      const payload = { code, lang };
      if (sub) payload.line_user_id = sub;
      if (idToken) payload.id_token = idToken;

      const { data } = await axios.post('/api/coupon/claim-by-line', payload);
      if (data?.ok) {
        handleClaimSuccess(data, { viaLine: true });
        return;
      }
      const errKey = data?.error || 'UNKNOWN';
      setLineErrorCode(errKey);
      if (errKey === 'ALREADY_CLAIMED' || errKey === 'ALREADY_USED') {
        setStatus('error');
        setMessage(t(errKey));
      } else {
        setMessage(t(errKey) !== errKey ? t(errKey) : t('lineFail'));
      }
    } catch (err) {
      const errKey = err?.response?.data?.error || 'NETWORK';
      const httpStatus = err?.response?.status;
      setLineErrorCode(httpStatus ? `${errKey} (${httpStatus})` : errKey);
      if (errKey === 'ALREADY_CLAIMED' || errKey === 'ALREADY_USED') {
        setStatus('error');
        setMessage(t(errKey));
      } else {
        setMessage(mapApiError(err) || t('lineFail'));
      }
    } finally {
      lineInflightRef.current = false;
      setBusy(null);
    }
  }, [code, lang, liffLineSub, loadingCoupon, t, couponInfo]);

  useEffect(() => {
    if (!code || !shouldAutoLineClaim() || loadingCoupon || status === 'success') return;
    if (lineAutoStartedRef.current) return;
    lineAutoStartedRef.current = true;
    runLineClaim();
  }, [code, loadingCoupon, status, runLineClaim]);

  const sendEmailCode = async (e) => {
    e?.preventDefault?.();
    if (resendSec > 0) return;
    const mobile = (phone || '').trim();
    const em = (email || '').trim();
    if (!mobile || !/^\+\d{7,15}$/.test(mobile)) {
      setMessage(t('enterPhone'));
      return;
    }
    if (!em || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
      setMessage(t('enterEmail'));
      return;
    }
    setBusy('send');
    setMessage('');
    try {
      const { data } = await axios.post('/api/coupon/send-email-code', {
        code, phone: mobile, email: em, lang,
      });
      if (data?.ok) {
        setVerifySessionId(data.verifySessionId);
        setStatus('verify');
        setResendSec(RESEND_COOLDOWN_SEC);
        setMessage(t('otpSent', { email: maskEmailForDisplay(em) }));
        return;
      }
      if (data?.error === 'EMAIL_RATE_LIMIT') {
        applyRateLimitFromResponse(data);
        return;
      }
      setMessage(t(data?.error) || t('sendFail'));
    } catch (err) {
      if (err?.response?.status === 429 && err?.response?.data?.error === 'EMAIL_RATE_LIMIT') {
        applyRateLimitFromResponse(err.response.data);
      } else {
        setMessage(mapApiError(err) || t('sendFail'));
      }
    } finally {
      setBusy(null);
    }
  };

  const submitClaim = async (e) => {
    e.preventDefault();
    const mobile = (phone || '').trim();
    const em = (email || '').trim();
    if (!otp.trim()) {
      setMessage(t('enterOtp'));
      return;
    }
    setBusy('claim');
    setMessage('');
    try {
      const { data } = await axios.post('/api/coupon/claim-by-phone', {
        code, phone: mobile, email: em, otp: otp.trim(), verifySessionId, lang,
      });
      if (data?.ok) {
        handleClaimSuccess(data);
      } else {
        setMessage(t(data?.error) || t('claimFail'));
      }
    } catch (err) {
      setMessage(mapApiError(err) || t('claimFail'));
    } finally {
      setBusy(null);
    }
  };

  const lineClaimUi = claimViaLine || isCouponLineClaimMode();
  const showBrowserForm = !lineClaimUi && status === 'form' && !loadingCoupon;

  const displayCoupon = useMemo(
    () => ({ ...couponInfo, ...claimResult }),
    [couponInfo, claimResult],
  );
  const couponDisplayName = useMemo(
    () => resolveCouponName(displayCoupon, lang),
    [displayCoupon, lang],
  );
  const couponDisplayDesc = useMemo(
    () => resolveCouponDescription(displayCoupon, lang),
    [displayCoupon, lang],
  );

  const linkUrl = buildCouponLinkReturnUrl(code);
  const currentStep = stepIndex(status);
  const emailMasked = maskEmailForDisplay(email);
  const successDisplayCode = claimResult
    ? resolveDisplayCode(claimResult.claimId, claimResult.displayCode)
    : '';

  const stepIndicator = (
    <div className="cc-steps">
      {STEPS.map((s, i) => (
        <React.Fragment key={s.key}>
          <div className={`cc-step${i <= currentStep ? ' on' : ''}${i === currentStep ? ' cur' : ''}`}>
            <span className="cc-step-num">{i + 1}</span>
            <span className="cc-step-label">{s.label}</span>
          </div>
          {i < STEPS.length - 1 && <div className={`cc-step-line${i < currentStep ? ' on' : ''}`} />}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <FullBleed bg={bg}>
      <StageFrame
        designW={1080}
        designH={1920}
        maxPcWidth={440}
        overlay={({ px, scale, w }) => {
          if (!w || !scale) return null;
          return (
          <>
            <style>{couponClaimStyles(px)}</style>
            <div className="cc-box">
            <CouponLangSwitch px={px} />
            <h1 className="cc-title">{t('claimTitle')}</h1>
            {status !== 'error' && !lineClaimUi && stepIndicator}
            {loadingCoupon ? (
              <p className="cc-sub">{t('loading')}</p>
            ) : couponInfo ? (
              <div className="cc-coupon-card">
                <div className="cc-coupon-name">{couponDisplayName}</div>
                {couponDisplayDesc && (
                  <div className="cc-coupon-desc">{couponDisplayDesc}</div>
                )}
              </div>
            ) : null}

            {lineClaimUi && status !== 'success' && !loadingCoupon && (
              <div className="cc-line-status">
                {busy === 'line' && (
                  <p className="cc-sub cc-line-auto">{message || t('lineAutoClaim')}</p>
                )}
                {busy !== 'line' && message && (
                  <p className={`cc-msg ${status === 'error' ? 'err' : 'warn'}`}>{message}</p>
                )}
                {lineErrorCode && busy !== 'line' && (
                  <p className="cc-sub" style={{ opacity: 0.7, fontSize: `${px(28)}px`, marginTop: `${px(8)}px` }}>
                    {t('lineErrorCode', { code: lineErrorCode })}
                  </p>
                )}
                {busy !== 'line' && status !== 'success' && (
                  <button
                    className="cc-btn cc-btn-primary"
                    type="button"
                    style={{ marginTop: `${px(16)}px` }}
                    onClick={() => {
                      lineAutoStartedRef.current = true;
                      runLineClaim();
                    }}
                  >
                    {t('lineRetryClaim')}
                  </button>
                )}
                {busy !== 'line' && lineErrorCode === 'NO_LINE_SUB' && (
                  <button
                    className="cc-btn cc-btn-secondary"
                    type="button"
                    style={{ marginTop: `${px(12)}px` }}
                    onClick={() => navigate(linkUrl)}
                  >
                    {t('bindCard')}
                  </button>
                )}
              </div>
            )}

            {showBrowserForm && (
              <div className="cc-form-block">
                <form onSubmit={sendEmailCode}>
                  <label className="cc-field-label" htmlFor="cc-phone">{t('phoneLabel')}</label>
                  <div className="cc-phone-wrap">
                    <PhoneInput
                      value={phone}
                      onChange={setPhone}
                      placeholder={t('phonePlaceholder')}
                      selectStyle={{
                        padding: `${px(14)}px ${px(8)}px`,
                        fontSize: `${px(36)}px`,
                        minWidth: 88,
                        borderRadius: `${px(14)}px`,
                        border: `${px(2)}px solid #111`,
                      }}
                      inputStyle={{
                        flex: 1,
                        padding: `${px(14)}px ${px(16)}px`,
                        fontSize: `${px(40)}px`,
                        textAlign: 'center',
                        borderRadius: `${px(14)}px`,
                        border: `${px(2)}px solid #111`,
                      }}
                    />
                  </div>
                  <label className="cc-field-label" htmlFor="cc-email">{t('emailLabel')}</label>
                  <input
                    id="cc-email"
                    className="cc-input"
                    type="email"
                    placeholder={t('emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                  />
                  <button
                    className="cc-btn cc-btn-primary"
                    type="submit"
                    disabled={busy === 'send' || resendSec > 0}
                  >
                    {busy === 'send'
                      ? t('sending')
                      : resendSec > 0
                        ? t('sendCodeCooldown', { sec: resendSec })
                        : t('sendCode')}
                  </button>
                </form>
              </div>
            )}

            {status === 'verify' && (
              <form onSubmit={submitClaim}>
                <p className="cc-sub">{t('verifySent')}</p>
                <p className="cc-email-highlight">{emailMasked}</p>
                <label className="cc-field-label" htmlFor="cc-otp">{t('otpLabel')}</label>
                <input
                  id="cc-otp"
                  className="cc-input cc-otp"
                  inputMode="numeric"
                  placeholder={t('otpPlaceholder')}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  autoFocus
                />
                <button
                  className="cc-btn cc-btn-primary"
                  type="submit"
                  disabled={busy === 'claim' || otp.length < 6}
                >
                  {busy === 'claim' ? t('claiming') : t('confirmClaim')}
                </button>
                <button
                  className="cc-btn cc-btn-secondary"
                  type="button"
                  disabled={resendSec > 0 || busy === 'send'}
                  onClick={sendEmailCode}
                >
                  {busy === 'send'
                    ? t('sending')
                    : resendSec > 0
                      ? t('resendCooldown', { sec: resendSec })
                      : t('resend')}
                </button>
                <button
                  className="cc-btn cc-btn-secondary"
                  type="button"
                  onClick={() => { setStatus('form'); setOtp(''); setMessage(''); }}
                >
                  {t('refill')}
                </button>
              </form>
            )}

            {status === 'error' && <div className="cc-msg err">{message}</div>}
            {(status === 'form' || status === 'verify') && message && busy !== 'line' && (
              <div className={`cc-msg ${status === 'verify' ? 'ok' : 'warn'}`}>{message}</div>
            )}

            {status === 'success' && claimResult && (
              <div className="cc-success-body">
                <div className="cc-msg ok">{message}</div>
                {claimViaLine ? (
                  <>
                    <div className="cc-coupon-card">
                      <div className="cc-coupon-name">{couponDisplayName}</div>
                    </div>
                    {!claimResult.memberFound && (
                      <button
                        className="cc-btn cc-btn-secondary"
                        type="button"
                        onClick={() => navigate(linkUrl)}
                      >
                        {t('bindCard')}
                      </button>
                    )}
                    <button
                      className="cc-btn cc-btn-primary"
                      type="button"
                      onClick={() => navigate('/card')}
                    >
                      {t('backToMemberCard')}
                    </button>
                  </>
                ) : (
                  <>
                    <CouponQrBlock
                      redeemToken={claimResult.redeemToken}
                      claimId={claimResult.claimId}
                      displayCode={successDisplayCode}
                      px={px}
                      size={Math.round(px(400))}
                      label={t('redeemCode')}
                    />
                    <p className="cc-note">{t('qrNote')}</p>
                    <button
                      className="cc-btn cc-btn-primary"
                      type="button"
                      onClick={() => navigate('/card')}
                    >
                      {t('goCard')}
                    </button>
                    {claimResult.memberFound ? (
                      <button
                        className="cc-btn cc-btn-secondary"
                        type="button"
                        onClick={() => navigate(`/coupons?highlightClaimId=${claimResult.claimId}`)}
                      >
                        {t('viewCoupons')}
                      </button>
                    ) : (
                      <button
                        className="cc-btn cc-btn-secondary"
                        type="button"
                        onClick={() => navigate(linkUrl)}
                      >
                        {t('bindCard')}
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
            </div>
          </>
          );
        }}
      />
    </FullBleed>
  );
}

export default function CouponClaim() {
  return (
    <CouponLocaleProvider>
      <CouponClaimBody />
    </CouponLocaleProvider>
  );
}
